/**
 * The project-level report (CLAUDE.md 8.1).
 *
 * "Concatenated selected positions with a table of contents, continuous page
 * numbering, and a consolidated QTO" -- the document an engineer actually issues. The
 * endpoint has existed since Phase A1 and nothing called it, so the only report the
 * product could produce was a single position's.
 *
 * FOR CONSTRUCTION is deliberately absent from the choices. The server authorises
 * that status against one approved position (`_authorise_doc_status`), so a project
 * report can never carry it, and offering a status that is always refused is worse
 * than not offering it.
 */
import { useState } from 'react'
import { Download, Eye, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { useCreateProjectReport, useReportPolling } from '@/api/hooks'
import type { DocStatus } from '@/api/types'
import { ReportPreviewDialog } from '@/components/calc/ReportPreviewDialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldLabel } from '@/components/ui/field'
import { useToast } from '@/components/ui/toast'
import { downloadFile } from '@/lib/download'
import { formatBytes } from '@/lib/format'

const PROJECT_DOC_STATUSES: { value: DocStatus; labelKey: string }[] = [
  { value: 'draft', labelKey: 'report.status.draft' },
  { value: 'review', labelKey: 'report.status.review' },
]

export function ProjectReportButton({
  projectId,
  open,
  onOpenChange,
}: {
  projectId: number
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t, i18n } = useTranslation(['common', 'report'])
  const { toast } = useToast()

  const [lang, setLang] = useState(i18n.language.slice(0, 2))
  const [docStatus, setDocStatus] = useState<DocStatus>('draft')
  const [reportId, setReportId] = useState<number | null>(null)
  const [previewing, setPreviewing] = useState(false)

  const createReport = useCreateProjectReport(projectId)
  const { data: report } = useReportPolling(reportId, reportId !== null)

  const onGenerate = async () => {
    try {
      const created = await createReport.mutateAsync({ lang, doc_status: docStatus })
      setReportId(created.id)
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof ApiError ? error.localised : undefined,
        variant: 'error',
      })
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('report.project')}</DialogTitle>
            <DialogDescription>{t('report.project_hint')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Field>
              <FieldLabel>{t('report.language')}</FieldLabel>
              <select
                value={lang}
                onChange={(event) => setLang(event.target.value)}
                className="h-11 w-full rounded-md border border-border bg-surface px-3 text-text"
              >
                <option value="en">English</option>
                <option value="fr">Français</option>
              </select>
            </Field>

            <Field description={t('report.project_construction_note')}>
              <FieldLabel>{t('report.doc_status')}</FieldLabel>
              <select
                value={docStatus}
                onChange={(event) => setDocStatus(event.target.value as DocStatus)}
                className="h-11 w-full rounded-md border border-border bg-surface px-3 text-text"
              >
                {PROJECT_DOC_STATUSES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey, { ns: 'report' })}
                  </option>
                ))}
              </select>
            </Field>

            {report && (
              <div
                aria-live="polite"
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3 text-sm"
              >
                <span>
                  {report.status === 'done'
                    ? `${formatBytes(report.byte_size)}${
                        report.page_count
                          ? ` · ${t('report.pages', { count: report.page_count })}`
                          : ''
                      }`
                    : report.status === 'error'
                      ? t(`errors:${report.error_key ?? 'error.report.render_failed'}`)
                      : t('report.generating')}
                </span>
                {report.status === 'done' && (
                  <span className="flex gap-2">
                    <Button size="sm" onClick={() => setPreviewing(true)}>
                      <Eye size={15} aria-hidden />
                      {t('action.view')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => void downloadFile(`/api/v1/reports/${report.id}/file`)}
                    >
                      <Download size={15} aria-hidden />
                      {t('action.download')}
                    </Button>
                  </span>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" onClick={() => onOpenChange(false)}>
              {t('action.close')}
            </Button>
            <Button
              type="button"
              variant="primary"
              loading={createReport.isPending}
              onClick={() => void onGenerate()}
            >
              <FileText size={15} aria-hidden />
              {t('report.generate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReportPreviewDialog
        reportId={previewing && report?.status === 'done' ? report.id : null}
        byteSize={report?.byte_size}
        pageCount={report?.page_count}
        onClose={() => setPreviewing(false)}
      />
    </>
  )
}
